import { motion } from "framer-motion";
import { fadeUp, staggerContainer, viewportOnce } from "../../lib/motion";

const ABOUT_IMG = "/about-pnw.png";

const CHAPTERS = [
  {
    n: "01",
    title: "We Start With You",
    body: "Before a single line of code, we listen. We learn how your team actually works, what keeps you up at night, and where your industry is heading next — so what we build fits your world, not ours.",
  },
  {
    n: "02",
    title: "Tools Built to Fit",
    body: "Every organization moves differently. We craft learning systems, mobile apps, and digital media around your real workflows — adaptable tools that feel like they were made for you, because they were.",
  },
  {
    n: "03",
    title: "In It for the Long Haul",
    body: "We don't disappear at launch. From tactical field conditions to enterprise rollouts, we stay alongside you — a steady partner helping you adapt to whatever comes next.",
  },
];

export const About = () => {
  return (
    <section id="about" className="section-pad relative" data-testid="about-section">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        {/* Header */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="grid grid-cols-1 gap-10 lg:grid-cols-12"
        >
          <motion.div variants={fadeUp} className="lg:col-span-4">
            <p className="overline overline-dark mb-6">/ About the Studio</p>
            <h2 className="font-display text-4xl leading-tight tracking-tight text-[#0C2340] md:text-5xl">
              Custom software,
              <span> built to last.</span>
            </h2>
          </motion.div>

          <motion.div variants={fadeUp} className="space-y-5 lg:col-span-7 lg:col-start-6" data-testid="about-verbatim">
            <p className="font-body text-base leading-relaxed text-[#1E3A5F] md:text-lg">
              Based in Whitehorse, Yukon, Tide Water Studios Inc. is a software
              development and technical consulting company specializing in custom
              digital solutions. We design and build learning management systems
              (LMS), mobile applications, web platforms, and digital media
              tailored to the unique needs of each client.
            </p>
            <p className="font-body text-base leading-relaxed text-[#1E3A5F] md:text-lg">
              Our work is driven by a practical approach to solving real-world
              problems. Whether supporting businesses, non-profit organizations,
              or public sector agencies, we create reliable, intuitive technology
              that improves workflows, strengthens training, and helps
              organizations adapt as they grow.
            </p>
            <p className="font-body text-base leading-relaxed text-[#1E3A5F] md:text-lg">
              With roots in the Pacific Northwest, Tide Water Studios combines
              modern software development with thoughtful design to deliver
              solutions that are flexible, dependable, and built to last. From
              concept to deployment, we work closely with our clients to create
              digital tools that support their goals today and evolve with them
              into the future.
            </p>
          </motion.div>
        </motion.div>

        {/* Image band */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="group relative mt-20 h-[42vh] overflow-hidden md:h-[56vh]"
        >
          <img src={ABOUT_IMG} alt="Pacific Northwest inlet — snow-capped peaks, forested islands, seabirds over calm water, seaweed-covered tide-pool rocks in the foreground" className="img-duotone h-full w-full object-cover" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-white/70 to-transparent" />
          <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10">
            <p className="overline overline-dark">/ Inspired by the Pacific Northwest</p>
          </div>
        </motion.div>

        {/* Numbered manifesto chapters */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mt-24 grid grid-cols-1 gap-x-12 gap-y-16 md:grid-cols-3"
        >
          {CHAPTERS.map((c) => (
            <motion.div key={c.n} variants={fadeUp} className="relative" data-testid={`about-chapter-${c.n}`}>
              <div className="metallic-number-dark font-display text-7xl leading-none md:text-8xl">
                {c.n}
              </div>
              <div className="hairline-light my-6 w-16" />
              <h3 className="font-display text-2xl text-[#0C2340] md:text-3xl">{c.title}</h3>
              <p className="mt-4 font-body text-base leading-relaxed text-[#1E3A5F]">{c.body}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
